"use client"

import { Dialog, Switch, Transition } from "@headlessui/react"
import { Fragment, useEffect, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import X from "@modules/common/icons/x"
import { useCookieConsent } from "./consent-context"

type Toggle = {
  key: "essential" | "analytics" | "marketing"
  label: string
  description: string
  required?: boolean
}

const TOGGLES: Toggle[] = [
  {
    key: "essential",
    label: "Essential",
    description:
      "Required for the site to function — session, cart, security, and consent itself. Cannot be disabled.",
    required: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Anonymous data on pages visited, time on site, and referral source (Google Analytics 4) so we can understand what's popular and improve the site.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Tracking of visitors and calls (Mediahawk dynamic phone numbers, Lead Forensics, Google Ads conversion tracking) and HubSpot live chat so we can measure campaign performance and respond to enquiries.",
  },
]

export default function CookieConsentPreferencesModal() {
  const {
    consent,
    isPreferencesOpen,
    closePreferences,
    savePreferences,
    acceptAll,
    rejectAll,
  } = useCookieConsent()

  // Local draft state — only commit on Save. Re-syncs to the persisted
  // consent every time the modal is opened.
  const [analytics, setAnalytics] = useState(false)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    if (isPreferencesOpen) {
      setAnalytics(consent?.analytics ?? false)
      setMarketing(consent?.marketing ?? false)
    }
  }, [isPreferencesOpen, consent])

  const valueFor = (key: Toggle["key"]) => {
    if (key === "essential") return true
    if (key === "analytics") return analytics
    return marketing
  }

  const setValueFor = (key: Toggle["key"], v: boolean) => {
    if (key === "analytics") setAnalytics(v)
    if (key === "marketing") setMarketing(v)
  }

  return (
    <Transition appear show={isPreferencesOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={closePreferences}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-aov-navy/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-[16px] bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-aov-border px-6 py-5">
                  <div>
                    <Dialog.Title className="font-display text-2xl font-bold uppercase tracking-wide text-aov-navy">
                      Cookie preferences
                    </Dialog.Title>
                    <Dialog.Description className="mt-1 text-sm text-aov-text-muted">
                      Choose which categories of cookies you'd like to allow.
                      You can change this at any time via the footer link.
                    </Dialog.Description>
                  </div>
                  <button
                    type="button"
                    onClick={closePreferences}
                    aria-label="Close"
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-aov-navy hover:bg-aov-surface"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="divide-y divide-aov-border">
                  {TOGGLES.map((t) => (
                    <div
                      key={t.key}
                      className="flex items-start justify-between gap-6 px-6 py-5"
                    >
                      <div>
                        <div className="font-display text-base font-bold text-aov-navy">
                          {t.label}
                          {t.required && (
                            <span className="ml-2 rounded-full bg-aov-navy/5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-aov-navy/70">
                              Always on
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-aov-text-muted leading-relaxed">
                          {t.description}
                        </p>
                      </div>
                      <Switch
                        checked={valueFor(t.key)}
                        onChange={(v) => !t.required && setValueFor(t.key, v)}
                        disabled={t.required}
                        className={`${
                          valueFor(t.key) ? "bg-aov-orange" : "bg-aov-border"
                        } ${
                          t.required ? "opacity-60 cursor-not-allowed" : ""
                        } relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors`}
                        aria-label={`${t.label} cookies`}
                      >
                        <span
                          className={`${
                            valueFor(t.key) ? "translate-x-6" : "translate-x-1"
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow`}
                        />
                      </Switch>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-aov-border px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
                  <LocalizedClientLink
                    href="/cookies"
                    className="text-xs font-semibold text-aov-orange hover:underline"
                    onClick={closePreferences}
                  >
                    Read full cookie policy →
                  </LocalizedClientLink>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={rejectAll}
                      className="inline-flex items-center justify-center rounded-[10px] border border-aov-navy/20 px-5 py-2.5 text-sm font-bold text-aov-navy transition-colors hover:bg-aov-navy/5"
                    >
                      Reject non-essential
                    </button>
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="inline-flex items-center justify-center rounded-[10px] border border-aov-navy/20 px-5 py-2.5 text-sm font-bold text-aov-navy transition-colors hover:bg-aov-navy/5"
                    >
                      Accept all
                    </button>
                    <button
                      type="button"
                      onClick={() => savePreferences({ analytics, marketing })}
                      className="inline-flex items-center justify-center rounded-[10px] bg-aov-orange px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-aov-orange/90"
                    >
                      Save preferences
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
